import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @extends {Component}
 */
export class AnnouncementBar extends Component {
  static get observedAttributes() {
    return ['autoplay'];
  }

  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Cancels stale section refreshes when cart updates happen quickly.
   * @type {AbortController | undefined}
   */
  #refreshAbortController = undefined;

  /**
   * Keeps autoplay in sync when Shopify re-renders blocks inside the section.
   * @type {MutationObserver}
   */
  #slidesObserver = new MutationObserver(() => {
    this.current = this.current;
    this.#syncPlayback();
  });

  /**
   * Restart autoplay when the theme editor updates the speed setting.
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'autoplay' || oldValue === newValue || !this.isConnected) return;

    this.suspend();
    this.play();
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('theme:countdown:hide', this.#handleCountdownHide);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#slidesObserver.observe(this, { childList: true, subtree: true });

    this.current = 0;
    this.#syncPlayback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('theme:countdown:hide', this.#handleCountdownHide);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#slidesObserver.disconnect();
    this.#refreshAbortController?.abort();
    this.suspend();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || !interval || this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.suspend();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval && this.slides.length > 1);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    const slides = this.slides;
    if (!slides.length) return;

    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) {
      relativeIndex += slides.length;
    }

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  get slides() {
    return Array.from(this.querySelectorAll('slideshow-slide[ref="slides[]"]:not(.hidden)'));
  }

  /**
   * Keeps expired countdown slides out of autoplay without re-rendering the section.
   * @param {Event} event
   */
  #handleCountdownHide = (event) => {
    if (!(event.target instanceof Element)) return;

    const slide = event.target.closest('slideshow-slide[ref="slides[]"]');
    slide?.classList.add('hidden');

    if (!this.slides.length) {
      this.closest('.shopify-section')?.classList.add('hidden');
      this.suspend();
      return;
    }

    this.current = this.current;
    this.#syncPlayback();
  };

  #syncPlayback() {
    if (!this.autoplay) {
      this.suspend();
      return;
    }

    if (!this.paused) {
      this.play();
    }
  }

  /**
   * Refreshes Liquid-rendered cart-dependent announcement content after Ajax cart changes.
   * @param {Event} event
   */
  #handleCartUpdate = async (event) => {
    const cartEvent = /** @type {import('@theme/events').CartUpdateEvent} */ (event);
    if (cartEvent.detail?.data?.didError) return;

    const sectionId = this.sectionId;
    if (!sectionId) return;

    this.#refreshAbortController?.abort();
    this.#refreshAbortController = new AbortController();

    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url.toString(), {
        signal: this.#refreshAbortController.signal,
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) return;

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const updatedSlides = documentFragment.querySelector('announcement-bar-component .announcement-bar__slides');
      const currentSlides = this.querySelector('.announcement-bar__slides');

      if (!currentSlides) return;

      if (!updatedSlides) {
        this.closest('.shopify-section')?.classList.add('hidden');
        this.suspend();
        return;
      }

      this.closest('.shopify-section')?.classList.remove('hidden');
      morph(currentSlides, updatedSlides);
      this.current = this.current;
      this.#syncPlayback();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
    }
  };

  get sectionId() {
    const section = this.closest('.shopify-section');
    return section?.id.replace(/^shopify-section-/, '');
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
